import { Add, ViewList } from '@mui/icons-material';
import { Box, Breadcrumbs, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import OrderDetailsModal from '../../components/order/OrderDetailsModal';
import OrderKanban from '../../components/order/OrderKanban';
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import { useListFetch } from '../../hooks/useListFetch';
import { applyOrderStageSteps } from '../../helpers/orderStages';
import { fetchAllOpenKanbanOrders } from '../../helpers/orders';

export default function OrdersKanbanPage() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [detailsModal, setDetailsModal] = useState({ open: false, orderId: null });

    const fetchOrders = useCallback(() => {
        fetchAllOpenKanbanOrders(api)
            .then((items) => {
                setOrders(items);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useListFetch(fetchOrders, [fetchOrders]);

    const replaceOrderInList = (orderId, nextOrder) => {
        setOrders((list) => list.map((item) => (item.id === orderId ? { ...item, ...nextOrder } : item)));
    };

    const handleKanbanComplete = async (order) => {
        const snapshot = orders;
        setOrders((list) => list.filter((item) => item.id !== order.id));
        try {
            await api.patch(`orders/${order.id}/stages`, { lane: 'completion', status: 'completed' });
        } catch (err) {
            setOrders(snapshot);
            Swal.fire(
                'Não foi possível concluir o pedido',
                err?.response?.data?.errors?.status
                    || err?.response?.data?.message
                    || 'Só é possível concluir após o pagamento e com logística entregue, não entregue ou devolvido.',
                'warning',
            );
        }
    };

    const handleKanbanMove = async (order, steps) => {
        const snapshot = orders;
        const optimistic = applyOrderStageSteps(order, steps);
        replaceOrderInList(order.id, optimistic);
        try {
            let serverOrder = optimistic;
            for (const step of steps) {
                const response = await api.patch(`orders/${order.id}/stages`, { lane: step.lane, status: step.status });
                if (response.data?.data) {
                    serverOrder = { ...optimistic, ...response.data.data };
                }
            }
            replaceOrderInList(order.id, serverOrder);
        } catch (err) {
            setOrders(snapshot);
            Swal.fire(
                'Não foi possível mover o pedido',
                err?.response?.data?.errors?.status
                    || err?.response?.data?.message
                    || 'A etapa escolhida não é válida para este pedido.',
                'warning',
            );
        }
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <Box
            sx={{
                height: 'calc(100dvh - 104px)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: 1.5,
                    mb: 1.5,
                    flexShrink: 0,
                }}
            >
                <Box>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                            Dashboard
                        </Link>
                        <Typography color="text.primary">Pedidos</Typography>
                        <Typography color="text.primary">Quadro de pedidos</Typography>
                    </Breadcrumbs>
                    <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 600 }}>
                        Pedidos
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/admin/orders/list')}
                        startIcon={<ViewList />}
                        fullWidth={isMobile}
                    >
                        Lista
                    </Button>
                    <QuotaLimitedButton
                        quotaKey="checkouts"
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/admin/orders/new')}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Pedido
                    </QuotaLimitedButton>
                </Box>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
                <OrderKanban
                    orders={orders}
                    onOpen={(orderId) => setDetailsModal({ open: true, orderId })}
                    onEdit={(orderId) => navigate(`/admin/orders/${orderId}/edit`)}
                    onMove={handleKanbanMove}
                    onComplete={handleKanbanComplete}
                />
            </Box>
            <OrderDetailsModal
                open={detailsModal.open}
                orderId={detailsModal.orderId}
                onClose={() => setDetailsModal({ open: false, orderId: null })}
            />
        </Box>
    );
}
